/**
 * "Generate Abby's voice" (spec 2026-10-23 §5 Tab 2, §6.4), as an engine with
 * its two network calls injected — so every rule is tested without a DOM or a
 * network (tests/unit/admin/app-experience-voice-generation.test.ts), and the
 * hook (../useVoiceGeneration.ts) only connects it to React and the API.
 *
 *   1. One call per paragraph (POST …/welcome/tts), at most VOICE_CONCURRENCY
 *      at a time — the first run and every retry share the same limit. The
 *      server answers `cached: true` when the paragraph already had its voice
 *      (no cost, no wait). A text that is in both languages is asked for once.
 *   2. When every paragraph of a language has its voice, ONE call joins them
 *      (POST …/welcome/assemble) with the TRIMMED paragraphs. A 409 names the
 *      paragraphs that have no voice yet; those rows fail, with Retry.
 *   3. What assemble returned is handed on unchanged (Publish sends it, R19),
 *      after one check that it is the voice for the text that was sent.
 *
 * Every answer is checked against the run it belongs to: a newer run, reset()
 * or an unmount makes a late answer change nothing.
 */
import {
  WELCOME_MAX_PARAGRAPHS,
  WELCOME_MIN_PARAGRAPHS,
  WELCOME_PARAGRAPH_MAX_CHARS,
  welcomeScriptHash,
  type ExperienceLocale,
  type WelcomeVoiceLocale,
} from './contract';
import { TOO_MANY_TEXT, errorFacts, isTimeout } from './error-words';
import { LOCALES } from './intro-state';
import { sameParagraphs, trimParagraphs } from './welcome-text';

export const VOICE_CONCURRENCY = 3;

/** Runs jobs with at most `limit` at a time, in the order they were asked for. */
export function createLimiter(limit: number) {
  let active = 0;
  const waiting: (() => void)[] = [];
  const next = () => {
    if (active >= limit) return;
    const start = waiting.shift();
    if (!start) return;
    active += 1;
    start();
  };
  return function schedule<T>(job: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      waiting.push(() => {
        let p: Promise<T>;
        try {
          p = job();
        } catch (e) {
          p = Promise.reject(e);
        }
        p.then(resolve, reject).finally(() => {
          active -= 1;
          next();
        });
      });
      next();
    });
  };
}

// ─── state ──────────────────────────────────────────────────────────────────

export type ParagraphStatus = 'queued' | 'generating' | 'cached' | 'done' | 'failed';

export interface TextState {
  status: ParagraphStatus;
  error: string | null;
}

export type LocalePhase = 'waiting' | 'assembling' | 'ready' | 'failed';

export interface LocaleRun {
  /** The trimmed paragraphs this run makes the voice for. */
  paragraphs: string[];
  phase: LocalePhase;
  /** Why it failed: some paragraphs have no voice, or joining them failed. */
  failure: 'paragraphs' | 'join' | null;
  error: string | null;
}

export interface GenerationSnapshot {
  running: boolean;
  locales: Partial<Record<ExperienceLocale, LocaleRun>>;
  /** Keyed by the paragraph text (a Map: a text may be any string). */
  texts: ReadonlyMap<string, TextState>;
}

export interface ParagraphRow {
  text: string;
  status: ParagraphStatus;
  error: string | null;
}

export const EMPTY_GENERATION: GenerationSnapshot = { running: false, locales: {}, texts: new Map() };

/** Where a text is used: the audit row of the TTS call names it (one text in both languages is asked for once: the first use). */
export interface TextUse {
  locale: ExperienceLocale;
  index: number;
}

export interface GenerationIO {
  synthesize(text: string, use: TextUse): Promise<{ cached: boolean }>;
  assemble(locale: ExperienceLocale, paragraphs: string[]): Promise<WelcomeVoiceLocale>;
}

export interface GenerationEvents {
  onUpdate(snapshot: GenerationSnapshot): void;
  onReady(locale: ExperienceLocale, voice: WelcomeVoiceLocale): void;
}

const NO_VOICE_YET = 'This paragraph has no voice yet. Use "Try again".';
const MISMATCH = "The voice that came back does not match the text. Generate Abby's voice again.";

function noVoiceText(n: number): string {
  return n === 1
    ? '1 paragraph has no voice yet. Use "Try again" on it.'
    : `${n} paragraphs have no voice yet. Use "Try again" on each one.`;
}

const settled = (s: ParagraphStatus) => s === 'cached' || s === 'done' || s === 'failed';
const ready = (s: ParagraphStatus) => s === 'cached' || s === 'done';

/** The paragraph numbers (from 0) that a 409 of assemble names. */
function missingOf(e: unknown): number[] {
  if (!e || typeof e !== 'object' || (e as { status?: unknown }).status !== 409) return [];
  const body = (e as { body?: unknown }).body;
  const missing = body && typeof body === 'object' ? (body as { missing?: unknown }).missing : undefined;
  return Array.isArray(missing) ? missing.filter((i): i is number => Number.isInteger(i)) : [];
}

export function createVoiceGeneration(io: GenerationIO, events: GenerationEvents, concurrency = VOICE_CONCURRENCY) {
  const limit = createLimiter(concurrency);
  let run = 0;
  let locales: Partial<Record<ExperienceLocale, LocaleRun>> = {};
  let texts = new Map<string, TextState>();

  const isRunning = () =>
    [...texts.values()].some((t) => t.status === 'queued' || t.status === 'generating') ||
    LOCALES.some((l) => locales[l]?.phase === 'assembling');

  const snapshot = (): GenerationSnapshot => ({ running: isRunning(), locales: { ...locales }, texts: new Map(texts) });
  const emit = () => events.onUpdate(snapshot());

  const setText = (text: string, state: TextState) => {
    texts = new Map(texts).set(text, state);
    emit();
  };
  const setLocale = (loc: ExperienceLocale, change: Partial<LocaleRun>) => {
    const r = locales[loc];
    if (!r) return;
    locales = { ...locales, [loc]: { ...r, ...change } };
    emit();
  };

  async function assemble(id: number, loc: ExperienceLocale): Promise<void> {
    const r = locales[loc];
    if (!r) return;
    setLocale(loc, { phase: 'assembling', failure: null, error: null });
    try {
      const voice = await io.assemble(loc, r.paragraphs);
      if (id !== run) return;
      if (voice.script_hash !== welcomeScriptHash(r.paragraphs) || !sameParagraphs(voice.paragraphs ?? [], r.paragraphs)) {
        setLocale(loc, { phase: 'failed', failure: 'join', error: MISMATCH });
        return;
      }
      setLocale(loc, { phase: 'ready' });
      events.onReady(loc, voice);
    } catch (e) {
      if (id !== run) return;
      const missing = missingOf(e);
      for (const i of missing) {
        const t = r.paragraphs[i];
        if (t !== undefined) setText(t, { status: 'failed', error: NO_VOICE_YET });
      }
      // The server's sentence for a 409 ("Make the voice for each paragraph first") is never shown.
      setLocale(loc, {
        phase: 'failed',
        failure: missing.length > 0 ? 'paragraphs' : 'join',
        error: missing.length > 0 ? noVoiceText(missing.length) : voiceErrorText(e),
      });
    }
  }

  /** After a paragraph settles: join each language whose paragraphs all have their voice. */
  async function settle(id: number): Promise<void> {
    const work: Promise<void>[] = [];
    for (const loc of LOCALES) {
      const r = locales[loc];
      if (!r || r.phase !== 'waiting') continue;
      const states = r.paragraphs.map((t) => texts.get(t)?.status ?? 'queued');
      if (!states.every(settled)) continue;
      const failed = states.filter((s) => s === 'failed').length;
      if (failed > 0) setLocale(loc, { phase: 'failed', failure: 'paragraphs', error: noVoiceText(failed) });
      else work.push(assemble(id, loc));
    }
    await Promise.all(work);
  }

  async function generate(id: number, text: string, use: TextUse): Promise<void> {
    await limit(async () => {
      if (id !== run) return;
      setText(text, { status: 'generating', error: null });
      try {
        const r = await io.synthesize(text, use);
        if (id !== run) return;
        setText(text, { status: r.cached ? 'cached' : 'done', error: null });
      } catch (e) {
        if (id !== run) return;
        setText(text, { status: 'failed', error: voiceErrorText(e) });
      }
    });
    if (id !== run) return;
    await settle(id);
  }

  return {
    snapshot,

    /** A new run for these languages (paragraphs as typed; trimmed here). Ends when every call has ended. */
    async start(input: Partial<Record<ExperienceLocale, readonly string[]>>): Promise<void> {
      run += 1;
      const id = run;
      locales = {};
      texts = new Map();
      const firstUse = new Map<string, TextUse>();
      for (const loc of LOCALES) {
        const ps = input[loc];
        if (!ps) continue;
        const paragraphs = trimParagraphs(ps);
        locales[loc] = { paragraphs, phase: 'waiting', failure: null, error: null };
        paragraphs.forEach((t, index) => {
          if (!texts.has(t)) texts.set(t, { status: 'queued', error: null });
          if (!firstUse.has(t)) firstUse.set(t, { locale: loc, index });
        });
      }
      emit();
      await Promise.all([...texts.keys()].map((t) => generate(id, t, firstUse.get(t)!)));
    },

    /** Try one failed paragraph again; then join its language(s) when they are complete. */
    async retry(locale: ExperienceLocale, index: number): Promise<void> {
      const text = locales[locale]?.paragraphs[index];
      if (text === undefined || texts.get(text)?.status !== 'failed') return;
      const id = run;
      for (const loc of LOCALES) {
        const r = locales[loc];
        if (r && r.failure === 'paragraphs' && r.paragraphs.includes(text)) {
          locales = { ...locales, [loc]: { ...r, phase: 'waiting', failure: null, error: null } };
        }
      }
      setText(text, { status: 'queued', error: null });
      await generate(id, text, { locale, index });
    },

    /** Join a language again after joining failed (its paragraphs all have their voice). */
    async retryJoin(locale: ExperienceLocale): Promise<void> {
      const r = locales[locale];
      if (!r || r.phase !== 'failed' || r.failure !== 'join') return;
      if (!r.paragraphs.every((t) => ready(texts.get(t)?.status ?? 'queued'))) return;
      await assemble(run, locale);
    },

    /** Forget everything; every late answer is ignored. */
    reset(): void {
      run += 1;
      locales = {};
      texts = new Map();
      emit();
    },
  };
}

export type VoiceGeneration = ReturnType<typeof createVoiceGeneration>;

// ─── reading a snapshot ─────────────────────────────────────────────────────

export function rowsOf(s: GenerationSnapshot, locale: ExperienceLocale): ParagraphRow[] {
  const r = s.locales[locale];
  if (!r) return [];
  return r.paragraphs.map((text) => {
    const t = s.texts.get(text) ?? { status: 'queued' as const, error: null };
    return { text, status: t.status, error: t.error };
  });
}

function allRows(s: GenerationSnapshot): ParagraphRow[] {
  return LOCALES.flatMap((l) => rowsOf(s, l));
}

const STATUS_LABEL: Record<ParagraphStatus, string> = {
  queued: 'Waiting',
  generating: 'Generating…',
  cached: 'Ready — reused, no cost',
  done: 'Ready',
  failed: 'Failed',
};

export function statusLabel(status: ParagraphStatus): string {
  return STATUS_LABEL[status];
}

/** "paragraph" or "paragraphs", by the count (one word, one meaning: never "1 paragraphs"). */
function paragraphsWord(n: number): string {
  return n === 1 ? 'paragraph' : 'paragraphs';
}

/** "5 of 18 paragraphs ready." and the failures, for the screen. */
export function progressText(s: GenerationSnapshot): string {
  const rows = allRows(s);
  const done = rows.filter((r) => ready(r.status)).length;
  const failed = rows.filter((r) => r.status === 'failed').length;
  return `${done} of ${rows.length} ${paragraphsWord(rows.length)} ready.${failed > 0 ? ` ${failed} failed.` : ''}`;
}

/**
 * The words of the polite live region. Progress in quarter steps (a screen
 * reader that read every paragraph would read nothing else), then the end:
 * ready, or what failed and what to do.
 */
export function spokenGenerationText(s: GenerationSnapshot): string {
  const rows = allRows(s);
  if (rows.length === 0) return '';
  const failedRows = rows.filter((r) => r.status === 'failed').length;
  if (s.running) {
    if (rows.every((r) => settled(r.status))) return 'Joining the paragraphs into one recording.';
    const step = Math.max(1, Math.ceil(rows.length / 4));
    const done = rows.filter((r) => ready(r.status)).length;
    return `Generating Abby's voice. ${Math.floor(done / step) * step} of ${rows.length} ${paragraphsWord(rows.length)} ready.`;
  }
  if (failedRows > 0) {
    return failedRows === 1
      ? '1 paragraph failed. Use "Try again" on it.'
      : `${failedRows} paragraphs failed. Use "Try again" on each one.`;
  }
  const failedLocale = LOCALES.map((l) => s.locales[l]).find((r) => r?.phase === 'failed');
  if (failedLocale) return `Abby's voice is not ready. ${failedLocale.error ?? ''}`.trim();
  const runs = LOCALES.map((l) => s.locales[l]).filter((r): r is LocaleRun => r !== undefined);
  if (runs.length > 0 && runs.every((r) => r.phase === 'ready')) {
    return "Abby's voice is ready. Listen to it below. Then publish.";
  }
  return '';
}

// ─── words for errors ───────────────────────────────────────────────────────

/**
 * Plain words for a failed voice call. The voice routes answer with a stable
 * code (`app_experience_tts_*`); each code gets the page's OWN words, which
 * name the controls the admin sees ("Generate Abby's voice", "Try again").
 * The server's sentence is read for nothing and shown never. Anything else —
 * a schema refusal, too many requests, a gateway timeout, a network fault —
 * becomes words an admin can act on too.
 */
export function voiceErrorText(e: unknown): string {
  const f = errorFacts(e);
  if (f.network) return 'The connection failed. Check the internet connection, then try again.';
  switch (f.code) {
    case 'app_experience_tts_unavailable':
      return 'The voice service did not answer. Wait one minute, then use "Try again".';
    case 'app_experience_tts_bad_audio':
      return "A stored voice file cannot be used. Generate Abby's voice again.";
    case 'app_experience_tts_record_missing':
      return "The voice file for this text cannot be checked. Tell engineering. Do not generate Abby's voice again: it will not help.";
    case 'app_experience_tts_missing':
      return 'Some paragraphs have no voice yet. Use "Try again" on each one.';
    case 'app_experience_tts_text':
      return `This paragraph cannot be read aloud. It must have text, at most ${WELCOME_PARAGRAPH_MAX_CHARS} characters, and no line break or hidden character. Correct it, then use "Try again".`;
    case 'app_experience_tts_paragraphs':
      return `Use ${WELCOME_MIN_PARAGRAPHS} to ${WELCOME_MAX_PARAGRAPHS} paragraphs. Then generate Abby's voice again.`;
    default:
      break;
  }
  if (f.status === 401) return 'Your session ended. Sign in again, then try again.';
  if (f.status === 403) return 'You do not have permission to change this page.';
  if (f.status === 429) return TOO_MANY_TEXT;
  if (isTimeout(f)) return 'The server did not answer in time. Wait one minute, then use "Try again".';
  if (f.status !== null && f.status >= 500) return `Abby's voice could not be made (error ${f.status}). Try again.`;
  if (f.status !== null && f.status >= 400) return 'The text could not be sent. Check the paragraph, then try again.';
  return 'Something went wrong. Try again.';
}
